import React, { useState, useEffect, useRef, useCallback } from "react";
import { uploadFileMetadata } from "../../../_services/documentsService";
import { KpiIcon } from "../Quantative/component/KpiIcons";
import DocumentPreviewModal from "./DocumentPreviewModal";

const fmtSize = (bytes) => {
  if (bytes == null) return "";
  const n = Number(bytes);
  if (Number.isNaN(n)) return "";
  return n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const docFileName = (doc) => doc?.fileMetadata?.fileName ?? doc?.fileName ?? "Document";
const docUrl = (doc) => doc?.fileMetadata?.url ?? null;
const docSize = (doc) => doc?.fileMetadata?.fileSize;

/**
 * Multi-document attach/remove field shared by the quantitative and
 * qualitative answer-save modals.
 *
 * Every save must resend the full desired end-state: `documentIds` for
 * existing documents to keep, plus `uploadedDocuments` for brand-new files.
 * Any previously-attached document not named in either list is removed
 * (and soft-deleted) server-side — so removing a row here is just a local
 * state change, no DELETE call.
 *
 * Visual language matches the original AddDataModal "choose a file" dropzone
 * and its attached-file card, kept consistent everywhere this field appears.
 *
 * Props:
 *   existingDocuments  DocumentDto[]  currently-attached documents
 *   resetKey           re-seeds kept/removed state when this changes
 *   onChange({ documentIds, uploadedDocuments, isUploading })
 *                      `isUploading` is true while any newly-picked file is still
 *                      being uploaded — callers must disable their Save/Continue
 *                      button on this, otherwise a save can fire before a file has
 *                      a fileMetadataId and silently drop that attachment.
 *   disabled
 *   hideDropzone       render only the attached-file list, no dropzone —
 *                      for a "review what you've attached" step once the
 *                      caller has its own separate "add more" entry point
 */
const MultiDocumentUploadField = ({ existingDocuments = [], resetKey, onChange, disabled, hideDropzone, previewVariant }) => {
  const [keptIds, setKeptIds] = useState(() => new Set(existingDocuments.map((d) => d.id)));
  const [newUploads, setNewUploads] = useState([]); // [{ tempId, file, fileMetadataId, fileMetadata, uploading, error }]
  const [dragOver, setDragOver] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState("");
  const [previewDoc, setPreviewDoc] = useState(null);
  const fileInputRef = useRef(null);
  const nextTempId = useRef(0);

  useEffect(() => {
    setKeptIds(new Set(existingDocuments.map((d) => d.id)));
    setNewUploads([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const emitChange = useCallback((nextKeptIds, nextUploads) => {
    onChange?.({
      documentIds: [...nextKeptIds],
      uploadedDocuments: nextUploads
        .filter((u) => u.fileMetadataId)
        .map((u) => ({ fileMetadataId: u.fileMetadataId, documentType: "EVIDENCE", fileMetadata: u.fileMetadata })),
      isUploading: nextUploads.some((u) => u.uploading),
    });
  }, [onChange]);

  const removeExisting = (id) => {
    setKeptIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      emitChange(next, newUploads);
      return next;
    });
  };

  const handleFiles = (fileList) => {
    const files = Array.from(fileList);
    const keptDocsList = existingDocuments.filter((d) => keptIds.has(d.id));
    const alreadyAttached = [
      ...keptDocsList.map((d) => ({ name: docFileName(d), size: docSize(d) })),
      ...newUploads.map((u) => ({ name: u.file.name, size: u.file.size })),
    ];

    const uniqueFiles = [];
    const duplicateNames = [];
    files.forEach((file) => {
      const isDuplicate = alreadyAttached.some((a) => a.name === file.name && a.size === file.size);
      if (isDuplicate) {
        duplicateNames.push(file.name);
      } else {
        uniqueFiles.push(file);
        alreadyAttached.push({ name: file.name, size: file.size });
      }
    });

    if (duplicateNames.length) {
      setDuplicateWarning(`Already attached, skipped: ${duplicateNames.join(", ")}`);
      setTimeout(() => setDuplicateWarning(""), 5000);
    }
    if (!uniqueFiles.length) return;

    const pending = uniqueFiles.map((file) => ({ tempId: nextTempId.current++, file, fileMetadataId: null, fileMetadata: null, uploading: true, error: null }));
    setNewUploads((prev) => {
      const next = [...prev, ...pending];
      emitChange(keptIds, next);
      return next;
    });

    pending.forEach(async (entry) => {
      try {
        const { fileMetadataId, fileMetadata } = await uploadFileMetadata(entry.file);
        setNewUploads((prev) => {
          const next = prev.map((u) => (u.tempId === entry.tempId ? { ...u, fileMetadataId, fileMetadata, uploading: false } : u));
          emitChange(keptIds, next);
          return next;
        });
      } catch (e) {
        setNewUploads((prev) => {
          const next = prev.map((u) => (u.tempId === entry.tempId ? { ...u, uploading: false, error: "Upload failed" } : u));
          emitChange(keptIds, next);
          return next;
        });
      }
    });
  };

  const removeNew = (tempId) => {
    setNewUploads((prev) => {
      const next = prev.filter((u) => u.tempId !== tempId);
      emitChange(keptIds, next);
      return next;
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  const keptDocs = existingDocuments.filter((d) => keptIds.has(d.id));

  const openPreview = (doc) => setPreviewDoc(doc);

  return (
    <div>
      {duplicateWarning && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 8, background: "#fffbeb", border: "1px solid #fde68a", fontSize: 11.5, color: "#92400e", fontWeight: 600, marginBottom: 10 }}>
          ⚠️ {duplicateWarning}
        </div>
      )}

      {hideDropzone && !keptDocs.length && !newUploads.length && (
        <div style={{ fontSize: 12, color: "var(--ink-4)", fontStyle: "italic", padding: "6px 2px" }}>
          No documents attached yet.
        </div>
      )}

      {keptDocs.map((doc) => (
        <div key={doc.id} style={{ borderRadius: 10, overflow: "hidden", border: "1px solid #bfdbfe", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "#eff6ff" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <span style={{ fontSize: 16 }}>📎</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 12, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 360 }}>
                  {docFileName(doc)}
                </div>
                {docSize(doc) != null && <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>{fmtSize(docSize(doc))}</div>}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
              {docUrl(doc) && (
                <button onClick={() => openPreview(doc)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--accent, #3f88a5)", fontWeight: 700, fontFamily: "var(--font-sans)", padding: "2px 6px" }}>
                  👁 preview
                </button>
              )}
              {!disabled && (
                <button onClick={() => removeExisting(doc.id)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--ink-4)", fontFamily: "var(--font-sans)", padding: "2px 6px" }}>
                  ✕ remove
                </button>
              )}
            </div>
          </div>
        </div>
      ))}

      {newUploads.map((u) => (
        <div key={u.tempId} style={{ borderRadius: 10, overflow: "hidden", border: `1px solid ${u.error ? "#fca5a5" : "#bfdbfe"}`, marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: u.error ? "var(--red-2)" : "#eff6ff" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <span style={{ fontSize: 16 }}>{u.uploading ? "⏳" : u.error ? "⚠️" : "📎"}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 12, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 360 }}>
                  {u.file.name}
                </div>
                <div style={{ fontSize: 11, color: u.error ? "var(--red)" : "var(--ink-3)", marginTop: 2 }}>
                  {u.uploading ? "Uploading…" : u.error ? u.error : `${fmtSize(u.file.size)} · saved as proof document`}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
              {u.fileMetadata?.url && (
                <button onClick={() => openPreview({ fileName: u.file.name, fileMetadata: u.fileMetadata })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--accent, #3f88a5)", fontWeight: 700, fontFamily: "var(--font-sans)", padding: "2px 6px" }}>
                  👁 preview
                </button>
              )}
              {!disabled && (
                <button onClick={() => removeNew(u.tempId)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--ink-4)", fontFamily: "var(--font-sans)", padding: "2px 6px" }}>
                  ✕ remove
                </button>
              )}
            </div>
          </div>
        </div>
      ))}

      {!disabled && !hideDropzone && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{ border: `2px dashed ${dragOver ? "var(--accent)" : "var(--rule)"}`, borderRadius: 12, padding: "32px 20px", textAlign: "center", cursor: "pointer", background: dragOver ? "var(--accent-3)" : "var(--surface-2)", transition: "all 0.15s ease" }}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => { if (e.target.files.length) handleFiles(e.target.files); e.target.value = ""; }}
          />
          <div style={{ fontSize: 38, marginBottom: 10 }}><KpiIcon name="upload" size={20} color="var(--ink4)" /></div>
          <div style={{ fontWeight: 700, fontSize: 14, color: "var(--ink)", marginBottom: 8 }}>Drop a file or click to browse</div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
            {[{ icon: "📄", label: "PDF / DOC", sub: "stored as proof" }, { icon: "🖼️", label: "JPG / PNG", sub: "stored as proof" }, { icon: "📊", label: "XLSX / CSV", sub: "stored as proof" }].map(({ icon, label, sub }) => (
              <div key={label} style={{ padding: "6px 12px", borderRadius: 8, background: "var(--surface)", border: "1px solid var(--rule)", textAlign: "left" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ink-2)" }}>{icon} {label}</div>
                <div style={{ fontSize: 10, color: "var(--ink-4)" }}>{sub}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <DocumentPreviewModal document={previewDoc} onClose={() => setPreviewDoc(null)} variant={previewVariant} />
    </div>
  );
};

export default MultiDocumentUploadField;
